import { z } from 'zod';
import {
  ESCALATION_DISPOSITION,
  escalationAsqAttemptRecencySchema,
  escalationDispositionSchema,
  escalationHandlingConcernSchema,
  type EscalationAsqResult,
  type EscalationDisposition,
  type EscalationHandlingConcern,
} from './schema';

// ─── the escalation handling decision ───────────────────────────────────────
//
// The structured decision a clinician records when closing a safety escalation:
// what the concern was, an ASQ suicide-risk screen when that concern is
// suicidal ideation, a resolution reason, details for "other", and a working
// note.
//
// Everything that decides whether a decision is valid lives in this one module
// and is imported by Atlas (to offer options and enable submit) and by the API
// (to validate the request and compute what is stored). The DB repeats the
// rules as CHECK constraints, but this is the source: a rule changed here and
// not there fails the real-DB integration suite.
//
// One input is NOT in the request: the member's minor status. A declined screen
// is scored differently for an adult and for a youth, so its result — and
// whether "false positive" may close it — depends on a fact only the server may
// resolve. The request schema therefore validates structure, and
// `isHandlingResolutionOffered` / `toStoredHandlingDecision` take the minor
// status as a parameter: the API passes the server-resolved value and its
// verdict is authoritative; Atlas passes the member projection's value for
// display only.

// ─── the ASQ screen ─────────────────────────────────────────────────────────

/** Q1–Q4: the screening items, always asked unless the screen is declined. */
export const ESCALATION_ASQ_SCREENING_ITEM_KEYS = ['asq_q1', 'asq_q2', 'asq_q3', 'asq_q4'] as const;
/** Q5: the acuity item, asked only once any of Q1–Q4 is Yes. */
export const ESCALATION_ASQ_ACUITY_ITEM_KEY = 'asq_q5';
export const ESCALATION_ASQ_ITEM_KEYS = [
  ...ESCALATION_ASQ_SCREENING_ITEM_KEYS,
  ESCALATION_ASQ_ACUITY_ITEM_KEY,
] as const;
export type EscalationAsqItemKey = (typeof ESCALATION_ASQ_ITEM_KEYS)[number];

export const ESCALATION_ASQ_Q5_DESCRIPTION_MAX_LENGTH = 2000;

/** `true` = Yes, `false` = No, `null` = not answered. */
const asqAnswerSchema = z.boolean().nullable();

/**
 * The NIMH ASQ as answered (revision 9/29/25). `declined` is the member
 * refusing the whole screen — an alternative to answering it, so a declined
 * screen carries no item, recency or description. There is no per-item
 * refusal: items are Yes/No.
 *
 * `asq_q4_recency` is the follow-up to a Yes on Q4 ("When was the most recent
 * attempt?"). `asq_q5_description` is the optional "Describe briefly" after a
 * Yes on Q5 — free text about a member's thoughts of suicide, held to the same
 * working-note posture as the handling notes.
 */
export const escalationAsqScreenSchema = z
  .object({
    declined: z.boolean(),
    asq_q1: asqAnswerSchema,
    asq_q2: asqAnswerSchema,
    asq_q3: asqAnswerSchema,
    asq_q4: asqAnswerSchema,
    asq_q5: asqAnswerSchema,
    asq_q4_recency: escalationAsqAttemptRecencySchema.nullable(),
    asq_q5_description: z.string().nullable(),
  })
  .strict();
export type EscalationAsqScreen = z.infer<typeof escalationAsqScreenSchema>;

export const EMPTY_ESCALATION_ASQ_SCREEN: EscalationAsqScreen = {
  declined: false,
  asq_q1: null,
  asq_q2: null,
  asq_q3: null,
  asq_q4: null,
  asq_q5: null,
  asq_q4_recency: null,
  asq_q5_description: null,
};

/** The acuity gate: Q5 is asked only when the screen is answered and any of Q1–Q4 is Yes. */
export function isAsqQ5Required(screen: EscalationAsqScreen): boolean {
  return !screen.declined && ESCALATION_ASQ_SCREENING_ITEM_KEYS.some((key) => screen[key] === true);
}

/** Whether Q4's attempt-recency follow-up is required. */
export function isAsqQ4RecencyRequired(screen: EscalationAsqScreen): boolean {
  return !screen.declined && screen.asq_q4 === true;
}

/**
 * The ASQ result, computed from the screen — the only place the rule exists.
 *
 * `isMinor` is the member's minor status (`null` when unknown) and only
 * matters for a declined screen. NIMH: an adult's refusal is not a positive
 * screen (`declined`); a youth's refusal is a non-acute positive. Unknown is
 * treated as a youth, because that is the stricter rule.
 *
 * `null` means the screen is incomplete and cannot be saved: any of Q1–Q4
 * unanswered, Q4's recency unanswered while Q4 is Yes, or Q5 unanswered while
 * the gate requires it. An unanswered Q1–Q4 is incomplete even when Q5 is Yes;
 * the screen is only recorded once every screening item has been asked.
 *
 * The gate is applied before scoring, so a Q5 answer while Q1–Q4 are all No is
 * ignored and the result is `negative`. That input is unreachable from Atlas
 * (the form clears Q5 when the gate closes) and is rejected by the request
 * schema and the DB; the ordering only makes this function total.
 */
export function computeAsqResult(
  screen: EscalationAsqScreen,
  isMinor: boolean | null,
): EscalationAsqResult | null {
  if (screen.declined) return isMinor === false ? 'declined' : 'non_acute_positive';
  const items = ESCALATION_ASQ_SCREENING_ITEM_KEYS.map((key) => screen[key]);
  if (items.some((answer) => answer === null)) return null;
  if (isAsqQ4RecencyRequired(screen) && screen.asq_q4_recency === null) return null;
  if (!isAsqQ5Required(screen)) return 'negative';
  const acuity = screen[ESCALATION_ASQ_ACUITY_ITEM_KEY];
  if (acuity === null) return null;
  return acuity ? 'acute_positive' : 'non_acute_positive';
}

/**
 * What is structurally wrong with a screen, independent of completeness: an
 * answer to a question that was not asked. Each is a client bug the API
 * rejects rather than silently clearing, so the stored screen is exactly what
 * the clinician saw.
 */
function asqStructureIssues(screen: EscalationAsqScreen): { path: string[]; message: string }[] {
  const issues: { path: string[]; message: string }[] = [];
  if (screen.declined) {
    const answered = [
      ...ESCALATION_ASQ_ITEM_KEYS.map((key) => screen[key]),
      screen.asq_q4_recency,
      screen.asq_q5_description,
    ].some((value) => value !== null);
    if (answered) {
      issues.push({ path: ['declined'], message: 'a declined screen carries no answers' });
    }
    return issues;
  }
  if (screen.asq_q5 !== null && !isAsqQ5Required(screen)) {
    issues.push({ path: ['asq_q5'], message: 'Q5 is only asked when any of Q1–Q4 is Yes' });
  }
  if (screen.asq_q4_recency !== null && screen.asq_q4 !== true) {
    issues.push({
      path: ['asq_q4_recency'],
      message: 'the attempt recency is only asked when Q4 is Yes',
    });
  }
  if (screen.asq_q5_description !== null && screen.asq_q5 !== true) {
    issues.push({
      path: ['asq_q5_description'],
      message: 'the description is only asked when Q5 is Yes',
    });
  }
  return issues;
}

// ─── allowed resolutions ────────────────────────────────────────────────────

/**
 * Whether one resolution may close an escalation with this concern and ASQ
 * result. Exhaustive over the vocabulary with no default, so a new disposition
 * is a compile error here rather than an option silently offered everywhere.
 *
 * A resolution must not contradict the screen: a positive screen means the
 * concern was real, so it cannot be a false positive, and an acute positive
 * (thoughts of killing themselves right now) cannot be closed as "contacted —
 * safe". An adult's declined screen is not a positive screen, so it behaves
 * like a negative one. The brief suicide safety assessment is NIMH's
 * follow-up to a positive ASQ; it is offered for any non-acute suicidal-
 * ideation screen (negative included — a clinician may run one on judgement,
 * a default still to be validated with the care team) and never for any
 * other concern, where it means nothing.
 */
function isResolutionOffered(
  disposition: EscalationDisposition,
  concern: EscalationHandlingConcern,
  asqResult: EscalationAsqResult | null,
): boolean {
  switch (disposition) {
    case 'contacted_member_safe':
      return asqResult !== 'acute_positive';
    case 'brief_safety_assessment_completed':
      return concern === 'suicidal_ideation' && asqResult !== 'acute_positive';
    case 'emergency_evaluation_initiated':
    case 'referred_988':
    case 'other':
      return true;
    case 'false_positive':
      return asqResult === null || asqResult === 'negative' || asqResult === 'declined';
  }
}

/**
 * The resolutions that may close an escalation with this concern and ASQ
 * result, in vocabulary order. `asqResult` is `null` for any concern other than
 * suicidal ideation, which carries no screen.
 */
export function allowedEscalationResolutions(
  concern: EscalationHandlingConcern,
  asqResult: EscalationAsqResult | null,
): readonly EscalationDisposition[] {
  return ESCALATION_DISPOSITION.filter((disposition) =>
    isResolutionOffered(disposition, concern, asqResult),
  );
}

// ─── the request ────────────────────────────────────────────────────────────

export const ESCALATION_RESOLUTION_DETAILS_MAX_LENGTH = 2000;
export const ESCALATION_HANDLING_NOTES_MAX_LENGTH = 4000;

/** Trimmed text, with blank folded to null. */
function trimmedOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Body of the staff POST that marks one escalation handled.
 *
 * `.strict()`: a request the server validates, so an unknown key is a client
 * bug to reject rather than drop (ADR-135). That is also what keeps a
 * client-supplied `asqResult` or minor status out — both are resolved by the
 * server — and what keeps a body-supplied resolver out: the acting clinician
 * comes from the authorized-staff gate, and a body field would be an
 * attribution forgery on a clinical record.
 *
 * Validates everything but one thing: whether "false positive" may close a
 * DECLINED screen, which depends on the member's minor status. For a declined
 * screen the schema accepts the adult set; `isHandlingResolutionOffered` is the
 * authoritative check once the status is known.
 *
 * The optional fields accept `null` as well as absence because Atlas posts
 * this schema's own OUTPUT, in which a blank note is already `null`. A schema
 * that could not re-parse its own output would reject every save without a
 * note.
 */
export const escalationHandlingDecisionSchema = z
  .object({
    concern: escalationHandlingConcernSchema,
    asq: escalationAsqScreenSchema.nullable().optional(),
    disposition: escalationDispositionSchema,
    resolutionDetails: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    const asq = body.asq ?? null;
    let asqResult: EscalationAsqResult | null = null;

    if (body.concern === 'suicidal_ideation') {
      if (asq === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['asq'],
          message: 'the ASQ screen is required when the concern is suicidal ideation',
        });
        return;
      }
      const issues = asqStructureIssues(asq);
      for (const issue of issues) {
        ctx.addIssue({ code: 'custom', path: ['asq', ...issue.path], message: issue.message });
      }
      if (issues.length > 0) return;
      // The adult reading: for an answered screen the minor status changes
      // nothing, and for a declined one this is the more permissive set.
      asqResult = computeAsqResult(asq, false);
      if (asqResult === null) {
        ctx.addIssue({ code: 'custom', path: ['asq'], message: 'the ASQ screen is incomplete' });
        return;
      }
      const description = trimmedOrNull(asq.asq_q5_description);
      if (description !== null && description.length > ESCALATION_ASQ_Q5_DESCRIPTION_MAX_LENGTH) {
        ctx.addIssue({
          code: 'custom',
          path: ['asq', 'asq_q5_description'],
          message: `the description exceeds ${ESCALATION_ASQ_Q5_DESCRIPTION_MAX_LENGTH} characters`,
        });
      }
    } else if (asq !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['asq'],
        message: 'the ASQ screen is only recorded when the concern is suicidal ideation',
      });
      return;
    }

    if (!isResolutionOffered(body.disposition, body.concern, asqResult)) {
      ctx.addIssue({
        code: 'custom',
        path: ['disposition'],
        message: 'this resolution is not offered for the concern and ASQ result',
      });
    }

    const details = trimmedOrNull(body.resolutionDetails);
    if (body.disposition === 'other') {
      if (details === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['resolutionDetails'],
          message: 'resolution details are required when the resolution is other',
        });
      }
    } else if (body.resolutionDetails !== undefined && body.resolutionDetails !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['resolutionDetails'],
        message: 'resolution details are only recorded when the resolution is other',
      });
    }
    if (details !== null && details.length > ESCALATION_RESOLUTION_DETAILS_MAX_LENGTH) {
      ctx.addIssue({
        code: 'custom',
        path: ['resolutionDetails'],
        message: `resolution details exceed ${ESCALATION_RESOLUTION_DETAILS_MAX_LENGTH} characters`,
      });
    }

    const notes = trimmedOrNull(body.notes);
    if (notes !== null && notes.length > ESCALATION_HANDLING_NOTES_MAX_LENGTH) {
      ctx.addIssue({
        code: 'custom',
        path: ['notes'],
        message: `notes exceed ${ESCALATION_HANDLING_NOTES_MAX_LENGTH} characters`,
      });
    }
  })
  .transform((body) => {
    const asq = body.concern === 'suicidal_ideation' ? (body.asq ?? null) : null;
    return {
      concern: body.concern,
      asq:
        asq === null ? null : { ...asq, asq_q5_description: trimmedOrNull(asq.asq_q5_description) },
      disposition: body.disposition,
      resolutionDetails:
        body.disposition === 'other' ? trimmedOrNull(body.resolutionDetails) : null,
      notes: trimmedOrNull(body.notes),
    };
  });

/** What Atlas sends — the schema's input shape. */
export type EscalationHandlingDecisionInput = z.input<typeof escalationHandlingDecisionSchema>;
/** A structurally valid, normalised decision — the schema's output shape. */
export type EscalationHandlingDecision = z.output<typeof escalationHandlingDecisionSchema>;

/** The ASQ result this decision records for a member with this minor status. */
export function decisionAsqResult(
  decision: EscalationHandlingDecision,
  isMinor: boolean | null,
): EscalationAsqResult | null {
  return decision.asq === null ? null : computeAsqResult(decision.asq, isMinor);
}

/**
 * The authoritative resolution check, once the member's minor status is known.
 * Differs from the schema's own check only for a declined screen: a youth's
 * (or unknown-status member's) refusal is a non-acute positive, which cannot
 * be closed as a false positive.
 */
export function isHandlingResolutionOffered(
  decision: EscalationHandlingDecision,
  isMinor: boolean | null,
): boolean {
  return isResolutionOffered(
    decision.disposition,
    decision.concern,
    decisionAsqResult(decision, isMinor),
  );
}
